// ---------------------------------------------------------------------------
// Reusable data table component
// ---------------------------------------------------------------------------

import React from 'react';

/** Column definition for the DataTable. */
export interface Column<T> {
  /** Unique key for this column. */
  key: string;
  /** Header label displayed in the table head. */
  header: string;
  /** Render function for the cell content. */
  render: (row: T) => React.ReactNode;
  /** Optional CSS class for the header cell. */
  headerClassName?: string;
  /** Optional CSS class for each body cell. */
  cellClassName?: string;
}

interface DataTableProps<T> {
  /** Column definitions. */
  columns: Column<T>[];
  /** Row data. */
  data: T[];
  /** Function to extract a unique key from each row. */
  rowKey: (row: T) => string;
  /** Message shown when data is empty. Defaults to "No data found". */
  emptyMessage?: string;
  /** Optional click handler for rows. */
  onRowClick?: (row: T) => void;
  /** Accessible label for the table. */
  ariaLabel?: string;
  /** Cursor for next page (from API response). */
  nextCursor?: string | null;
  /** Callback when user clicks "Next". */
  onNextPage?: (cursor: string) => void;
  /** Callback when user clicks "Previous" (client-managed). */
  onPreviousPage?: () => void;
  /** Whether there's a previous page. */
  hasPreviousPage?: boolean;
  /** Page size. */
  pageSize?: number;
  /** Total count hint (if available). */
  totalHint?: string;
}

export function DataTable<T>({
  columns,
  data,
  rowKey,
  emptyMessage = 'No data found',
  onRowClick,
  ariaLabel,
  nextCursor,
  onNextPage,
  onPreviousPage,
  hasPreviousPage,
  pageSize,
  totalHint,
}: DataTableProps<T>) {
  const showPagination =
    pageSize !== undefined || nextCursor || hasPreviousPage || onNextPage || onPreviousPage;

  const isNestedInteractiveTarget = (target: EventTarget | null, currentTarget: HTMLElement): boolean => {
    if (!(target instanceof HTMLElement)) return false;

    const interactiveTarget = target.closest('a, button, input, select, textarea, [role="button"]');
    return Boolean(interactiveTarget && interactiveTarget !== currentTarget);
  };

  return (
    <div className="bg-surface-raised rounded-sm border border-hairline overflow-hidden">
      <div className="overflow-x-auto">
        <table
          className="min-w-full divide-y divide-hairline"
          aria-label={ariaLabel}
        >
          <thead className="bg-surface-sunken">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  className={`px-6 py-2.5 text-start font-mono text-[0.6875rem] font-medium text-ink-muted uppercase tracking-[0.08em] ${col.headerClassName ?? ''}`}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-hairline [&>tr:nth-child(even)]:bg-surface-sunken/40">
            {data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-6 py-8 text-center text-ink-muted"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((row) => (
                <tr
                  key={rowKey(row)}
                  className={`transition-colors hover:bg-primary-tint/50 ${onRowClick ? 'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary' : ''}`}
                  onClick={onRowClick ? (e) => {
                    if (!isNestedInteractiveTarget(e.target, e.currentTarget)) {
                      onRowClick(row);
                    }
                  } : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={
                    onRowClick
                      ? (e) => {
                          if (!isNestedInteractiveTarget(e.target, e.currentTarget) && (e.key === 'Enter' || e.key === ' ')) {
                            e.preventDefault();
                            onRowClick(row);
                          }
                        }
                      : undefined
                  }
                  role={onRowClick ? 'button' : undefined}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={`px-6 py-3.5 text-sm text-ink ${col.cellClassName ?? ''}`}
                    >
                      {col.render(row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      {showPagination && (
        <nav
          className="flex items-center justify-between border-t border-hairline bg-surface-sunken px-6 py-3"
          aria-label="Table pagination"
        >
          <p className="text-xs text-ink-muted">
            Showing <span className="font-mono text-ink">{data.length}</span> item{data.length !== 1 ? 's' : ''}
            {totalHint ? <> of <span className="font-mono text-ink">{totalHint}</span></> : ''}
            {pageSize ? <> (page size <span className="font-mono">{pageSize}</span>)</> : ''}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={!hasPreviousPage}
              onClick={onPreviousPage}
              className="px-3 py-1.5 text-sm font-medium rounded-sm border border-hairline-strong bg-white text-ink hover:border-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-hairline-strong"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={!nextCursor}
              onClick={nextCursor && onNextPage ? () => onNextPage(nextCursor) : undefined}
              className="px-3 py-1.5 text-sm font-medium rounded-sm border border-hairline-strong bg-white text-ink hover:border-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-hairline-strong"
            >
              Next
            </button>
          </div>
        </nav>
      )}
    </div>
  );
}
